import { FC } from "react";
import { Stack, Box, LinearProgress, Divider } from "@mui/material";
import { useOutletContext } from "react-router-dom";
import { useDidMount } from "rooks";
import { grey } from "@mui/material/colors";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { InfrastructureArchitectureSelectPanel } from "@/components/pages/infrastructure-architecture/components/select-panel/InfrastructureArchitectureSelectPanel";
import { InfrastructureArchitectureRecommendationPanel } from "@/components/pages/infrastructure-architecture/components/recommendation-panel/InfrastructureArchitectureRecommendationPanel";
import { InfrastructureArchitectureDiagram } from "@/components/pages/infrastructure-architecture/components/select-panel/InfrastructureArchitectureDiagram";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { OutletContextType } from "@/components/layout/MainLayout";
import { initiateInfrastructureArchitectureThunk } from "@/store/infrastructure-architecture/thunks/initiateInfrastructureArchitectureThunk";
import { infrastructureArchitectureDiagramEmptyDataSelector } from "@/store/infrastructure-architecture/selectors/infrastructureArchitectureDiagramEmptyDataSelector";
import { infrastructureArchitecturePossibleServicesRegionsLoadingSelector } from "@/store/infrastructure-architecture/selectors/infrastructureArchitecturePossibleServicesRegionsLoadingSelector";
import { InfrastructureArchitectureDiagramSpecialCases } from "@/components/pages/infrastructure-architecture/components/InfrastructureArchitectureDiagramSpecialCases";
import { infrastructureArchitectureRenderDiagramSpecialCasesSelector } from "@/store/infrastructure-architecture/selectors/infrastructureArchitectureRenderDiagramSpecialCasesSelector";

export const InfrastructureArchitecture: FC = () => {
  const dispatch = useAppDispatch();
  const possibleRegionsAndServicesLoading = useAppSelector(
    infrastructureArchitecturePossibleServicesRegionsLoadingSelector,
  );
  const diagramDataEmpty = useAppSelector(
    infrastructureArchitectureDiagramEmptyDataSelector,
  );
  const renderDiagramSpecialCases = useAppSelector(
    infrastructureArchitectureRenderDiagramSpecialCasesSelector,
  );

  const { collapseNavigation } = useOutletContext<OutletContextType>();

  useDidMount(() => {
    collapseNavigation();
    dispatch(initiateInfrastructureArchitectureThunk());
  });

  return (
    <Stack height="100%" sx={dotBackground}>
      {possibleRegionsAndServicesLoading && (
        <Box position="absolute" top={0} left={0} right={0}>
          <LinearProgress />
        </Box>
      )}

      <PanelGroup direction="horizontal" autoSaveId={autoSaveId}>
        <InfrastructureArchitectureSelectPanel />

        <PanelResizeHandle>
          <Divider orientation="vertical" />
        </PanelResizeHandle>

        <Panel style={{ overflowY: "auto" }} order={2} id={diagramId}>
          {!diagramDataEmpty && <InfrastructureArchitectureDiagram />}

          {renderDiagramSpecialCases && (
            <InfrastructureArchitectureDiagramSpecialCases />
          )}
        </Panel>

        <PanelResizeHandle>
          <Divider orientation="vertical" />
        </PanelResizeHandle>

        <InfrastructureArchitectureRecommendationPanel />
      </PanelGroup>
    </Stack>
  );
};

const autoSaveId = "infrastructure-architecture-panel";
const diagramId = "infrastructure-architecture-diagram";

const dotBackground = {
  backgroundColor: "white",
  backgroundImage: `radial-gradient(circle, ${grey[200]} 2px, transparent 2px)`,
  backgroundSize: "20px 20px",
};
